import React from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { generateImage, serializeDocument } from '../utils/imageServerApi';
import { useAlert } from '../hooks/useAlert';

type PlaceholderMap = { [key: string]: string };

interface CustomFont {
  id: string;
  font_name: string;
  font_family_css: string;
  font_file_url: string;
}

function extractPlaceholders(html: string): string[] {
  const set = new Set<string>();
  html.replace(/\{\{\s*([a-zA-Z0-9_-]+)\s*\}\}/g, (_m, k) => { set.add(String(k)); return ''; });
  return Array.from(set);
}

interface RunnerProps {
  template: string;
  canvasWidth?: number;
  canvasHeight?: number;
  defaultValues?: any[]; // العناصر من المشروع
  projectName?: string;
}

export default function Runner({ template: initialTemplate, canvasWidth = 800, canvasHeight = 600, defaultValues = [], projectName }: RunnerProps) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { showAlert, AlertComponent } = useAlert();
  const [template] = React.useState<string>(initialTemplate);
  const [placeholders, setPlaceholders] = React.useState<string[]>(extractPlaceholders(initialTemplate));
  
  // استخراج القيم الافتراضية من عناصر المشروع (فقط العناصر الظاهرة في Runner)
  const getDefaultValuesFromElements = () => {
    const defaults: PlaceholderMap = {};
    
    defaultValues.forEach((el: any) => {
      // Skip hidden elements
      if (el.hideInRunner) return;
      
      // استخدم الـ name من العنصر - يكون بالفعل محتوي على text- أو img-
      const key = el.name || (el.type === 'text' ? `text-${el.order !== undefined ? el.order + 1 : 1}` : `img-${el.order !== undefined ? el.order + 1 : 1}`);
      
      if (el.type === 'text') {
        defaults[key] = el.text || '';
      } else if (el.type === 'image') {
        defaults[key] = el.src || '';
      }
    });
    
    return defaults;
  };
  
  const getPlaceholderTypesFromElements = () => {
    const types: Record<string, 'text' | 'image'> = {};
    defaultValues.forEach((el: any) => {
      if (el.hideInRunner) return;
      const key = el.name || (el.type === 'text' ? `text-${el.order !== undefined ? el.order + 1 : 1}` : `img-${el.order !== undefined ? el.order + 1 : 1}`);
      if (el.type === 'text' || el.type === 'image') {
        types[key] = el.type;
      }
    });
    return types;
  };

  const [values, setValues] = React.useState<PlaceholderMap>(getDefaultValuesFromElements());
  const [placeholderTypes, setPlaceholderTypes] = React.useState<Record<string, 'text' | 'image'>>(getPlaceholderTypesFromElements());
  const [customFonts, setCustomFonts] = React.useState<CustomFont[]>([]);
  const [generatedImage, setGeneratedImage] = React.useState<string | null>(null);
  const frameRef = React.useRef<HTMLIFrameElement>(null);
  const [imageCache, setImageCache] = React.useState<Map<string, string>>(new Map());
  const [imagesLoading, setImagesLoading] = React.useState(true);
  const [imagesReady, setImagesReady] = React.useState(false);
  
  // Load custom fonts
  React.useEffect(() => {
    const loadCustomFonts = async () => {
      if (!user) return;
      
      try {
        console.log('🔄 Loading custom fonts for Runner...');
        const { data, error } = await supabase
          .from('custom_fonts')
          .select('*')
          .eq('user_id', user.id);
        
        if (!error && data) {
          console.log('✅ Loaded custom fonts:', data.length);
          setCustomFonts(data);
          
          // حقن الخطوط في الصفحة الرئيسية أيضاً
          const styleId = 'runner-custom-fonts-style';
          let styleEl = document.getElementById(styleId) as HTMLStyleElement;
          if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = styleId;
            document.head.appendChild(styleEl);
          }
          
          const fontFaces = data.map((font: CustomFont) => {
            const familyName = font.font_family_css.replace(/['"]/g, '').split(',')[0].trim();
            console.log(`🔤 Injecting font: ${familyName} from ${font.font_file_url}`);
            return `@font-face {
              font-family: '${familyName}';
              src: url('${font.font_file_url}') format('truetype');
              font-weight: normal;
              font-style: normal;
              font-display: swap;
            }`;
          }).join('\n');
          
          styleEl.textContent = fontFaces;
          console.log('✅ Injected custom fonts into main document');
        } else {
          console.warn('⚠️ No custom fonts found or error:', error);
        }
      } catch (err) {
        console.error('❌ Error loading custom fonts:', err);
      }
    };
    loadCustomFonts();
  }, [user]);
  
  // Reset values & types when project elements change
  React.useEffect(() => {
    setValues(getDefaultValuesFromElements());
    setPlaceholderTypes(getPlaceholderTypesFromElements());
  }, [defaultValues]);

  // Preload images when template loads or defaultValues change
  React.useEffect(() => {
    const loadImages = async () => {
      if (!template) {
        setImagesLoading(false);
        setImagesReady(true);
        return;
      }
      
      setImagesLoading(true);
      setImagesReady(false);
      console.log('🖼️ جاري تحميل صور المشروع...');
      console.log('📋 عناصر المشروع (defaultValues):', defaultValues);
      
      try {
        // Extract ALL image URLs from defaultValues (project elements)
        // Including hidden ones to ensure they're cached when needed
        const imageUrls: string[] = [];
        defaultValues.forEach((el: any) => {
          console.log('🔍 فحص عنصر:', { 
            type: el.type, 
            src: el.src?.substring(0, 50), 
            hideInRunner: el.hideInRunner 
          });
          
          if (el.type === 'image' && el.src && !el.src.startsWith('data:')) {
            console.log('✅ إضافة صورة للتحميل:', el.src.substring(0, 50));
            imageUrls.push(el.src);
            
            if (el.hideInRunner) {
              console.log('ℹ️ الصورة مخفية لكن سيتم تحميلها في الكاش');
            }
          }
        });
        
        console.log(`📥 وجدت ${imageUrls.length} صورة في المشروع (جميع الصور بما فيها المخفية)`);
        
        // Preload all images
        const cache = new Map<string, string>();
        let loaded = 0;
        
        for (const url of imageUrls) {
          try {
            console.log(`📥 تحميل صورة ${loaded + 1}/${imageUrls.length}: ${url.substring(0, 50)}...`);
            const response = await fetch(url);
            const blob = await response.blob();
            const dataUrl = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(blob);
            });
            cache.set(url, dataUrl);
            loaded++;
            console.log(`✅ تم تحميل ${loaded}/${imageUrls.length}`);
          } catch (err) {
            console.error(`❌ فشل تحميل الصورة: ${url}`, err);
          }
        }
        
        setImageCache(cache);
        console.log(`✅ تم تحميل ${cache.size} صورة بنجاح`);
        
        // Update values with cached images
        console.log('🔄 تحديث values بالصور المحملة...');
        setValues(prev => {
          const updated = { ...prev };
          
          defaultValues.forEach((el: any) => {
            if (el.hideInRunner) return;
            
            if (el.type === 'image' && el.src && !el.src.startsWith('data:')) {
              // استخدم الـ name من العنصر
              const key = el.name || `img${el.order !== undefined ? el.order + 1 : 1}`;
              const cachedDataUri = cache.get(el.src);
              
              if (cachedDataUri) {
                updated[key] = cachedDataUri;
                console.log(`✅ تم تحديث ${key} بالصورة المحملة`);
              } else {
                updated[key] = el.src; // Fallback to original URL
                console.log(`⚠️ ${key} لم يتم تحميلها، استخدام الرابط الأصلي`);
              }
            }
          });
          
          return updated;
        });
        
        console.log('✅ تم تحديث جميع الصور في values');
        
        // Mark as ready
        setImagesReady(true);
      } catch (err) {
        console.error('خطأ في تحميل الصور:', err);
        // Even on error, allow user to proceed
        setImagesReady(true);
      } finally {
        setImagesLoading(false);
      }
    };
    
    loadImages();
  }, [template, defaultValues]);

  // Update placeholders when template changes
  React.useEffect(() => {
    const keys = extractPlaceholders(template);
    setPlaceholders(keys);
    setValues(prev => {
      const next: PlaceholderMap = { ...prev };
      keys.forEach(k => { if (!(k in next)) next[k] = ''; });
      Object.keys(next).forEach(k => { if (!keys.includes(k)) delete next[k]; });
      return next;
    });
  }, [template]);

  const applyTemplate = React.useCallback(() => {
    // replace placeholders
    let html = template;
    Object.entries(values).forEach(([k, v]) => {
      const re = new RegExp(`\\{\\{\\s*${k}\\s*\\}}`, 'g');
      html = html.replace(re, v || '');
    });
    
    // inject font override CSS and custom font faces
    const headCloseIdx = html.indexOf('</head>');
    
    // Add @font-face for custom fonts
    const fontFaces = customFonts.map(f => {
      const familyName = f.font_family_css.replace(/['"]/g, '').split(',')[0].trim();
      console.log(`🔤 Adding font: ${familyName} from ${f.font_file_url}`);
      return `@font-face{font-family:'${familyName}';src:url('${f.font_file_url}') format('truetype');font-weight:normal;font-style:normal;font-display:swap;}`;
    }).join('');
    
    console.log('🔤 Injecting fonts into iframe:', customFonts.length, 'fonts');
    console.log('📝 Font faces:', fontFaces.substring(0, 200) + '...');
    
    // Script to auto-fit text elements
    const autoFitScript = `
      <script>
        (function() {
          window.processElements = function() {
            const textElements = document.querySelectorAll('#canvas > div[data-key]');
            textElements.forEach(div => {
              if (div.querySelector('img')) return;
              
              const text = div.textContent || '';
              if (!text.trim()) return;
              
              const containerHeight = div.offsetHeight;
              if (containerHeight <= 0) return;
              
              if (div.scrollHeight > containerHeight + 2) {
                let currentFontSize = parseFloat(window.getComputedStyle(div).fontSize);
                const minFontSize = 8;
                
                while (div.scrollHeight > containerHeight && currentFontSize > minFontSize) {
                  currentFontSize -= 0.5;
                  div.style.fontSize = currentFontSize + 'px';
                }
              }
            });
          };
          
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', window.processElements);
          } else {
            window.processElements();
          }
          window.addEventListener('load', window.processElements);
        })();
      </script>
    `;
    
    const inject = `<style>${fontFaces}</style>${autoFitScript}`;
    
    if (headCloseIdx !== -1) {
      html = html.slice(0, headCloseIdx) + inject + html.slice(headCloseIdx);
    } else {
      html = html.replace('<body>', `<head>${inject}</head><body>`);
    }
    
    console.log('📄 Final HTML length:', html.length);
    
    // render inside iframe via srcdoc for live update
    if (frameRef.current) {
      frameRef.current.srcdoc = html;
    }
  }, [template, values, customFonts]);

  React.useEffect(() => {
    applyTemplate();
  }, [applyTemplate]);

  const [exporting, setExporting] = React.useState(false);

  const handleValueChange = (k: string, v: string) => setValues(prev => ({ ...prev, [k]: v }));
  
  const handleImageUpload = (key: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      handleValueChange(key, dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const exportImage = async () => {
    setExporting(true);
    try {
      // Check credits using new FIFO system
      if (user) {
        const { data: creditsValue, error: creditsError } = await supabase
          .rpc('get_user_available_credits', { p_user_id: user.id });
        
        const remaining = Number(creditsValue) || 0;
        if (remaining <= 0) {
          showAlert({
            title: t('components.runner.insufficientCredits'),
            message: t('components.runner.insufficientCreditsMessage', { credits: remaining }),
            type: 'error'
          });
          setExporting(false);
          return;
        }
      }
      
      if (!frameRef.current) return;
      const doc = frameRef.current.contentDocument;
      if (!doc) {
        showAlert({
          title: t('components.runner.pleaseWait'),
          message: t('components.runner.loadingPreview'),
          type: 'warning'
        });
        setExporting(false);
        return;
      }
      
      console.log('📸 توليد الصورة...');
      
      // تحويل الـ document إلى HTML string
      const htmlString = serializeDocument(doc);
      
      // تحضير الخطوط المخصصة لإرسالها إلى سيرفر الرندر
      const fontsForRender = customFonts.map(font => ({
        name: font.font_name,
        url: font.font_file_url
      }));

      console.log(`📤 إرسال الطلب مع ${fontsForRender.length} خط مخصص...`);
      
      if (fontsForRender.length > 0) {
        console.log('📝 تفاصيل الخطوط:');
        fontsForRender.forEach((font, index) => {
          console.log(`   ${index + 1}. ${font.name} - ${font.url}`);
        });
      }
      
      // استدعاء API مع الخطوط المخصصة
      const result = await generateImage({
        html: htmlString,
        width: canvasWidth,
        height: canvasHeight,
        fonts: fontsForRender,
        quality: 100
      });
      
      if (!result.success) {
        throw new Error(result.error || t('components.runner.generationFailed'));
      }
      
      if (!result.image) {
        throw new Error(t('components.runner.noImageReturned'));
      }
      
      console.log('✅ تم توليد الصورة بنجاح');
      
      // Debug: Check image size
      const imageSize = result.image.length;
      console.log(`📊 Image data size: ${(imageSize / 1024).toFixed(2)} KB`);
      console.log(`🖼️ Image preview: ${result.image.substring(0, 100)}...`);

      // Save image to state instead of downloading
      setGeneratedImage(result.image);
      
      // Use credits for rendering (1 credit per image)
      if (user) {
        try {
          const { data: creditResult, error } = await supabase.rpc('use_credits_fifo', {
            p_user_id: user.id,
            p_amount: 1,
            p_reason: t('components.runner.generationReason')
          });
          
          if (error) {
            console.warn('Failed to use credits:', error);
          } else if (creditResult && !creditResult.success) {
            console.warn('Credit usage failed:', creditResult.error);
          }
        } catch (err) {
          console.warn('Failed to use credits:', err);
        }
      }
    } catch (err) {
      console.error('Export error:', err);
      showAlert({
        title: t('components.runner.exportFailed'),
        message: (err as Error).message,
        type: 'error'
      });
    } finally {
      setExporting(false);
    }
  };

  // Show loading overlay if images aren't ready
  if (!imagesReady) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md bg-zinc-900/80 backdrop-blur-lg border border-zinc-800 rounded-2xl shadow-2xl p-8 sm:p-12">
          <div className="flex flex-col items-center justify-center gap-6">
            <svg className="animate-spin h-16 w-16 sm:h-20 sm:w-20 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <div className="text-center w-full">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                {t('components.runner.pleaseWait')}
              </h3>
              <p className="text-gray-400 text-base sm:text-lg">
                {t('components.runner.loadingProject')}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <AlertComponent />
      <div className="w-full flex items-center justify-center relative">
      {/* Full Screen Loading Overlay */}
      {exporting && !generatedImage && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="bg-zinc-900 rounded-2xl p-8 flex flex-col items-center gap-4 border border-zinc-700 shadow-2xl">
            <svg className="animate-spin h-16 w-16 text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-white text-xl font-bold">{t('components.runner.creatingImage')}</p>
            <p className="text-gray-400 text-sm">{t('components.runner.pleaseWait')}</p>
          </div>
        </div>
      )}
      
      {/* Generated Image Modal */}
      {generatedImage && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-[9999] flex items-center justify-center p-4 overflow-auto">
          <div className="bg-zinc-900 rounded-2xl p-6 sm:p-8 max-w-5xl w-full border border-zinc-700 shadow-2xl my-auto">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-white mb-2">{t('components.runner.imageCreated')}</h3>
              <p className="text-gray-400">{t('components.runner.imageCreatedMessage')}</p>
            </div>
            
            {/* Image Preview */}
            <div className="bg-black/40 rounded-xl p-4 sm:p-6 mb-6 flex items-center justify-center">
              <img 
                src={generatedImage} 
                alt="Generated" 
                className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.download = 'template-output.png';
                  link.href = generatedImage;
                  link.click();
                }}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span className="text-lg">{t('components.runner.downloadImage')}</span>
              </button>
              <button
                onClick={() => setGeneratedImage(null)}
                className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="text-lg">{t('common.back')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Inputs Card - Centered */}
      <div className="w-full max-w-3xl bg-zinc-900/80 backdrop-blur-lg border border-zinc-800 rounded-2xl shadow-2xl p-4 sm:p-6 md:p-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">{projectName || t('components.runner.runTemplate')}</h2>
          <p className="text-gray-400">{t('components.runner.enterData')}</p>
        </div>
        
        {/* Inputs */}
        <div className="mb-8">
          <div className="flex flex-col gap-6">
            {placeholders.length === 0 && <div className="text-gray-500 text-center py-8">{t('components.runner.noInputs')}</div>}
            {placeholders.map(k => {
              const placeholderType = placeholderTypes[k] || (k.startsWith('img') ? 'image' : 'text');
              const isImg = placeholderType === 'image';
              return (
                <div key={k}>
                  <label className="block text-base font-semibold mb-3 text-gray-300">{k}</label>
                  {isImg ? (
                    <div className="space-y-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(k, file);
                        }}
                        className="w-full bg-zinc-800 border-2 border-zinc-700 hover:border-zinc-600 focus:border-blue-500 text-white px-4 py-3 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 transition-all cursor-pointer"
                      />
                      <p className="text-sm text-gray-400">{t('components.runner.selectNewImage')}</p>
                    </div>
                  ) : (
                    <textarea
                      value={values[k] || ''}
                      onChange={e => handleValueChange(k, e.target.value)}
                      rows={placeholderType === 'text' ? 4 : 1}
                      placeholder={t('components.runner.enterPlaceholder', { key: k })}
                      className="w-full bg-zinc-800 text-white border border-zinc-700 hover:border-zinc-600 focus:border-blue-500 rounded-lg p-4 text-base resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                      style={{ fontFamily: 'inherit' }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Generate Button */}
        <div className="pt-4 border-t border-zinc-700">
          <button 
            onClick={exportImage}
            disabled={exporting}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold py-5 px-8 rounded-xl transition-all flex items-center justify-center gap-3 shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]"
          >
            {exporting ? (
              <>
                <svg className="animate-spin h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-xl">{t('components.runner.creatingImage')}</span>
              </>
            ) : (
              <>
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-xl">{t('components.runner.createImage')}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview - Hidden but functional for image generation */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0, visibility: 'hidden' }}>
        <iframe 
          ref={frameRef} 
          title="runner" 
          style={{ 
            width: `${canvasWidth}px`,
            height: `${canvasHeight}px`,
            border: 'none'
          }} 
        />
      </div>
    </div>
    </>
  );
}
